import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../../components/navbar';
import { FaEdit, FaTrash, FaLock, FaLockOpen, FaSearch, FaUser, FaEnvelope, FaPhone, FaWrench, FaPlus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

const ManageTechnicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false); // NEW: Add modal state
  const [currentTechnician, setCurrentTechnician] = useState(null);
  const [newTechnician, setNewTechnician] = useState({ // NEW: State for new technician
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    phoneNumber: '',
    role: 'technician',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState({ open: false, type: '', id: '' });

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/technicians`);
      setTechnicians(response.data);
    } catch (err) {
      setError('Failed to fetch technicians');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteTechnician = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/technicians/${id}`);
      fetchTechnicians();
      toast.success('Technician deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete technician');
    }
  };

  const blockTechnician = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/technicians/${id}/block`);
      fetchTechnicians();
      toast.success('Technician blocked successfully');
    } catch (err) {
      console.error('Block error:', err);
      toast.error('Failed to block technician');
    }
  };

  const unblockTechnician = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/admin/technicians/${id}/unblock`);
      fetchTechnicians();
      toast.success('Technician unblocked successfully');
    } catch (err) {
      console.error('Unblock error:', err);
      toast.error('Failed to unblock technician');
    }
  };

  const openEditModal = (technician) => {
    setCurrentTechnician(technician);
    setIsEditModalOpen(true);
  };

  // NEW: Function to open add technician modal
  const openAddModal = () => {
    setIsAddModalOpen(true);
    setNewTechnician({
      firstname: '',
      lastname: '',
      email: '',
      phoneNumber: '',
      role: 'technician',
    });
  };

  const updateTechnician = async (updatedData) => {
    try {
      await axios.put(`${API_URL}/api/admin/technicians/${currentTechnician._id}`, updatedData);
      fetchTechnicians();
      setIsEditModalOpen(false);
      toast.success('Technician updated successfully');
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update technician');
    }
  };

  // NEW: Function to add a new technician
  const addTechnician = async () => {
    const { firstname, lastname, email, phoneNumber, password, role } = newTechnician;

    // Validate required fields
    if (!firstname || !lastname || !email || !password) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Check password strength
    const passwordPattern =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordPattern.test(password)) {
      toast.error(
        'Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.'
      );
      return;
    }

    try {
      await axios.post(`${API_URL}/api/auth/signup/technician`, {
        firstname,
        lastname,
        email,
        phoneNumber,
        password,
        role,
      });
      fetchTechnicians();
      setIsAddModalOpen(false);
      toast.success('Technician added successfully');
    } catch (err) {
      console.error('Add error:', err);
      const errorMessage =
        err.response?.data?.msg || 'Failed to add technician. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTechnician(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // NEW: Handler for new technician form
  const handleNewInputChange = (e) => {
    const { name, value } = e.target;
    setNewTechnician(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredTechnicians = technicians.filter(technician => {
    const term = searchTerm.toLowerCase();
    
    return (
      (technician.username && technician.username.toLowerCase().includes(term)) ||
      (technician.email && technician.email.toLowerCase().includes(term)) ||
      (technician.firstname && technician.firstname.toLowerCase().includes(term)) ||
      (technician.lastname && technician.lastname.toLowerCase().includes(term)) ||
      (technician.phoneNumber && technician.phoneNumber.toLowerCase().includes(term)) // Added phone to search
    );
  });

  const handleConfirmAction = () => {
    switch (confirmAction.type) {
      case 'delete':
        deleteTechnician(confirmAction.id);
        break;
      case 'block':
        blockTechnician(confirmAction.id);
        break;
      case 'unblock':
        unblockTechnician(confirmAction.id);
        break;
      default:
        break;
    }
    setConfirmAction({ open: false, type: '', id: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-6 pt-28 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Manage Technicians</h1>
            <p className="text-gray-600 mt-1">View and manage all technicians</p>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-0 w-full md:w-auto">
            {/* NEW: Add Technician button */}
            <button
              onClick={openAddModal}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FaPlus className="mr-2" />
              Add Technician
            </button>
            
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search technicians..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : filteredTechnicians.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No technicians found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? "No technicians match your search." : "There are no registered technicians yet."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Technician
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTechnicians.map(technician => (
                    <tr key={technician._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FaUser className="text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {technician.firstname || 'N/A'} {technician.lastname || ''}
                            </div>
                            {/* <div className="text-sm text-gray-500">
                              @{technician.username || 'N/A'}
                            </div> */}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm flex items-center text-gray-900">
                          <FaEnvelope className="mr-2 text-gray-400" />
                          {technician.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {technician.phoneNumber ? (
                          <div className="text-sm text-gray-500 flex items-center">
                            <FaPhone className="mr-2 text-gray-400" />
                            {technician.phoneNumber}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No phone</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          technician.isBlocked 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {technician.isBlocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(technician)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                            title="Edit technician"
                          >
                            <FaEdit />
                          </button>
                          
                          {technician.isBlocked ? (
                            <button
                              onClick={() => setConfirmAction({ open: true, type: 'unblock', id: technician._id })}
                              className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50"
                              title="Unblock technician"
                            >
                              <FaLockOpen />
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirmAction({ open: true, type: 'block', id: technician._id })}
                              className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-50"
                              title="Block technician"
                            >
                              <FaLock />
                            </button>
                          )}
                          
                          <button
                            onClick={() => setConfirmAction({ open: true, type: 'delete', id: technician._id })}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                            title="Delete technician"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Technician Modal */}
      {isEditModalOpen && currentTechnician && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-xl font-semibold text-gray-800">Edit Technician</h3>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="firstname"
                      value={currentTechnician.firstname || ''}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="lastname"
                      value={currentTechnician.lastname || ''}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={currentTechnician.email || ''}
                      onChange={handleInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* ADDED: Phone number field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={currentTechnician.phoneNumber || ''}
                      onChange={handleInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaWrench className="text-gray-400" />
                    </div>
                    <select
                      name="role"
                      value={currentTechnician.role || 'technician'}
                      onChange={handleInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="technician">Technician</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateTechnician(currentTechnician)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Add Technician Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-xl font-semibold text-gray-800">Add New Technician</h3>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="firstname"
                      value={newTechnician.firstname}
                      onChange={handleNewInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="lastname"
                      value={newTechnician.lastname}
                      onChange={handleNewInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={newTechnician.email}
                      onChange={handleNewInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={newTechnician.password}
                    onChange={handleNewInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="Strong password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={newTechnician.phoneNumber}
                      onChange={handleNewInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaWrench className="text-gray-400" />
                    </div>
                    <select
                      name="role"
                      value={newTechnician.role}
                      onChange={handleNewInputChange}
                      className="w-full pl-10 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="technician">Technician</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addTechnician}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Add Technician
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction.open && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex flex-col items-center">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                confirmAction.type === 'delete' ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                {confirmAction.type === 'delete' ? (
                  <FaTrash className="h-6 w-6 text-red-600" />
                ) : (
                  <FaLock className="h-6 w-6 text-blue-600" />
                )}
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {confirmAction.type === 'delete' 
                  ? 'Delete Technician' 
                  : confirmAction.type === 'block' 
                    ? 'Block Technician' 
                    : 'Unblock Technician'}
              </h3>
              <div className="mt-2 text-sm text-gray-500 text-center">
                <p>
                  {confirmAction.type === 'delete' 
                    ? 'Are you sure you want to delete this technician? This action cannot be undone.' 
                    : confirmAction.type === 'block' 
                      ? 'Are you sure you want to block this technician? They will no longer be able to access the system.'
                      : 'Are you sure you want to unblock this technician? They will regain access to the system.'}
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center space-x-3">
              <button
                onClick={() => setConfirmAction({ open: false, type: '', id: '' })}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`px-4 py-2 rounded-md text-white ${
                  confirmAction.type === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : confirmAction.type === 'block' 
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                } transition-colors`}
              >
                {confirmAction.type === 'delete' 
                  ? 'Delete' 
                  : confirmAction.type === 'block' 
                    ? 'Block' 
                    : 'Unblock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTechnicians;